import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { PropertyValues } from 'lit';
import { DPromoDialog } from 'src/library/promo/d-promo-dialog';
import { features } from '../../store/selectors/features/features';
import type { OrganizationViewModelSpecialTermsEnum } from '@trinnvis/dabih-api-client';
import { promo } from 'src/store/selectors/promo';
import type { FeatureStates } from '../../store/selectors/featureStates';
import { userHasModule } from '../../store/selectors/features/userHasModule';
import type { PromoDialogResult } from '../../library/promo/promo-dialog-result';
import type { PageMenuItem } from './page-menu-item';
import type { PageMenuGroup } from './page-menu-group';
import { pageIds } from 'src/store/selectors/pages/pageIds';

/**
 *
 */
@customElement('d-page-menu')
export class DPageMenu extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      list-style-type: none;
      margin: 0 20px;
      padding-bottom: 100px;
      font-family: var(--mainSans);
      direction: ltr;
    }
    ul {
      list-style-type: none;
      margin: 0;
      padding: 0;
    }
    .group {
      padding: 6px 0;
      margin-bottom: 12px;
      border-radius: 12px;
      background: hsla(0, 0%, 0%, 0.2);
      overflow: hidden;
    }
    .group-name {
      margin-top: -6px;
      padding: 10px 12px 2px 12px;
      color: white;
      font-size: 11px;
      line-height: 150%;
      font-weight: 400;
      text-transform: uppercase;
      letter-spacing: 1px;
      opacity: 0.7;
    }
    .group-name.expandable {
      cursor: pointer;
    }
    .group-name.expandable:before {
      content: '';
      display: inline-block;
      margin-right: 3px;
      width: 0;
      height: 0;
      border-top: 4px solid transparent;
      border-left: 8px solid white;
      border-bottom: 4px solid transparent;
      transition: transform 0.3s;
    }
    .group-name.expandable.expanded:before {
      transform: rotate(90deg);
    }
    .group li {
      display: block;
      padding: 2px 12px;
      color: var(--themeColorLighterTwo);
      text-decoration: none;
      font-weight: normal;
      cursor: pointer;
      line-height: 150%;
    }
    .group li.inactive {
      font-weight: 200;
      opacity: 0.6;
    }
    .group li[selected] {
      color: white;
      cursor: default;
    }
    @media (hover: hover) {
      .group-name.expandable:hover {
        opacity: 1;
      }
      .group li:not([selected]):hover {
        color: hsl(196, 83%, 90%);
      }
    }
  `;
  @property({ type: Number })
  setScrolltop = 0;
  @property({ type: Number })
  organizationId = 0;
  @property({ type: Array })
  pageMenu: PageMenuGroup[] = [];
  @property({ type: Number })
  pageId = 0;
  @property({ type: Number })
  currentLevel = 0;
  @property({ type: Object })
  featureStates!: FeatureStates;
  @property({ type: Number })
  employeesCount = 1;
  @property({ type: String })
  specialTerms: OrganizationViewModelSpecialTermsEnum | undefined = undefined;
  @property({ type: Boolean })
  isAppPlatform = false;
  @property({ type: Boolean, attribute: 'write-access', reflect: true })
  writeAccess = false;
  @property({ type: Boolean })
  onlyPerformanceReview = false;

  handleScroll(e) {
    this.dispatchEvent(
      new CustomEvent('scrolltop-changed', {
        bubbles: true,
        composed: true,
        detail: e.target.scrollTop,
      }),
    );
  }

  async onMenuClick(page: PageMenuItem) {
    if (page.href) {
      this.dispatchEvent(new CustomEvent('navigate', { bubbles: true, composed: true, detail: { href: page.href } }));
    } else {
      const promoData = promo.find((p) => {
        return p.id === page.promo;
      });
      if (promoData) {
        const filteredPromoData = {
          ...promoData,
          modules: promoData.modules.filter((m) => !userHasModule(m, this.featureStates)),
        };
        const result: PromoDialogResult = await DPromoDialog.open({
          promoData: filteredPromoData,
          features: features,
          employeesCount: this.employeesCount,
          specialTerms: this.specialTerms,
          healthcareSignatoryOptions: [],
          healthcareSignatory: undefined,
          canUpgrade: this.writeAccess,
          hasHealthcare: this.featureStates.healthcare,
        });
        if (result.action === 'upgrade' && promoData.pageAfterUpgrade)
          this.dispatchEvent(
            new CustomEvent('navigate', {
              bubbles: true,
              composed: true,
              detail: { href: '/account/' + this.organizationId + '/' + promoData.pageAfterUpgrade },
            }),
          );
      }
    }
  }

  render() {
    return html`
      <ul>
        ${this.pageMenu
          .filter((group) => {
            // Hide empty page groups
            if (!group.pages || group.pages.length === 0) return false;
            // Hide promo (inactive) groups for users with only performanceReview
            if (this.onlyPerformanceReview && group.code === 'promo') return false;
            return true;
          })
          .map((group) => {
            // Filter pages within each group
            const filteredPages = group.pages.filter((page) => {
              // Hide trash page for users without write access
              if (page.pageId === pageIds.trash && !this.writeAccess) {
                return false;
              }
              // Hide screens page in mobile app
              if (page.pageId === pageIds.screens && this.isAppPlatform) {
                return false;
              }
              return true;
            });

            // Don't render group if all pages are filtered out
            if (filteredPages.length === 0) return nothing;

            return html`
              <li>
                <ul class="group">
                  ${group.name ? html` <div class="group-name">${group.name}</div> ` : nothing}
                  ${filteredPages.map(
                    (page) =>
                      html`<li
                        class="item ${group.code === 'promo' ? 'inactive' : ''}"
                        ?selected="${page.pageId === this.pageId && this.currentLevel > 0}"
                        @click=${() => this.onMenuClick(page)}
                      >
                        ${page.name}
                      </li> `,
                  )}
                </ul>
              </li>
            `;
          })}
      </ul>
    `;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.addEventListener('scroll', this.handleScroll);
  }

  protected updated(_changedProperties: PropertyValues) {
    if (_changedProperties.has('setScrolltop')) {
      this.scrollTo({
        top: this.setScrolltop,
        left: 0,
        behavior: 'smooth',
      });
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-page-menu': DPageMenu;
  }
}
